import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Code, Shield, Users, Zap, ChevronDown, ChevronUp, Heart } from 'lucide-react';
import PageLayout from '../layout/PageLayout';
import { weebManifesto } from '../../data/philosophy';
import { detailedCodingPhilosophy } from '../../data/philosophyExpanded';

const PhilosophyPageExpanded: React.FC = () => {
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({});

  const toggleSection = (id: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const principleIcons = [Shield, Code, Zap, Users, BookOpen, Heart, Heart];

  return (
    <PageLayout>
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl md:text-6xl font-theme font-bold mb-6 text-gradient">
              Our Philosophy
            </h1>
            <p className="text-xl md:text-2xl mb-4" style={{ color: 'var(--color-text-secondary)' }}>
              How we think, how we code, and why we probably overthink everything
            </p>
            <p className="text-lg" style={{ color: 'var(--color-text)' }}>
              This page is split into two parts: Our weeb principles, and how we actually code when we're being serious.
            </p>
          </motion.div>
        </div>
      </section>

      {/* The Weeb Manifesto */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-theme font-bold mb-4 text-gradient">
                The Weeb Manifesto
              </h2>
              <p className="text-lg" style={{ color: 'var(--color-text-secondary)' }}>
                Our core principles (and why they matter)
              </p>
            </div>

            <div className="space-y-8">
              {weebManifesto.map((principle, index) => (
                <motion.div
                  key={principle.number}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="p-8 rounded-2xl"
                  style={{
                    backgroundColor: 'var(--color-bg-secondary)',
                    borderColor: 'var(--color-border)',
                    borderWidth: '1px',
                    borderStyle: 'solid'
                  }}
                >
                  <div className="flex items-start space-x-4">
                    <div
                      className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-2xl font-theme font-bold"
                      style={{
                        background: 'var(--gradient-primary)',
                        color: 'var(--color-bg)'
                      }}
                    >
                      {principle.number}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-theme font-bold mb-2" style={{ color: 'var(--color-primary)' }}>
                        {principle.title}
                      </h3>
                      <p className="text-lg mb-4" style={{ color: 'var(--color-text)' }}>
                        {principle.description}
                      </p>
                      <ul className="space-y-2">
                        {principle.expanded.map((point, i) => (
                          <li key={i} className="flex items-start space-x-2">
                            <span style={{ color: 'var(--color-primary)' }}>▸</span>
                            <span style={{ color: 'var(--color-text-secondary)' }}>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Divider */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="border-t" style={{ borderColor: 'var(--color-border)' }} />
        </div>
      </section>

      {/* How We Actually Code */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-theme font-bold mb-4 text-gradient">
                How We Actually Code
              </h2>
              <p className="text-lg mb-6" style={{ color: 'var(--color-text-secondary)' }}>
                (When we're not making weeb jokes)
              </p>
              <p className="text-base max-w-3xl mx-auto" style={{ color: 'var(--color-text)' }}>
                These are the real engineering principles that guide our work. This is the stuff we actually care about when building software. Click each section to expand and dive deep into the philosophy.
              </p>
            </div>

            {/* Detailed Principles - Expandable */}
            <div className="space-y-6">
              {detailedCodingPhilosophy.map((principle, index) => {
                const Icon = principleIcons[index % principleIcons.length];
                const isExpanded = expandedSections[principle.id];

                return (
                  <motion.div
                    key={principle.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    className="rounded-xl overflow-hidden"
                    style={{
                      backgroundColor: 'var(--color-bg-secondary)',
                      borderColor: isExpanded ? 'var(--color-primary)' : 'var(--color-border)',
                      borderWidth: isExpanded ? '2px' : '1px',
                      borderStyle: 'solid'
                    }}
                  >
                    {/* Header - Always Visible */}
                    <button
                      onClick={() => toggleSection(principle.id)}
                      className="w-full p-6 flex items-start justify-between hover:bg-opacity-50 transition-all"
                      style={{
                        backgroundColor: isExpanded ? 'var(--color-primary)' + '10' : 'transparent'
                      }}
                    >
                      <div className="flex items-start space-x-4 flex-1 text-left">
                        <div
                          className="p-3 rounded-lg flex-shrink-0"
                          style={{ backgroundColor: 'var(--color-primary)' + '20' }}
                        >
                          <Icon size={28} style={{ color: 'var(--color-primary)' }} />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl md:text-2xl font-theme font-bold mb-1" style={{ color: 'var(--color-primary)' }}>
                            {principle.title}
                          </h3>
                          <p className="text-base" style={{ color: 'var(--color-text-secondary)' }}>
                            {principle.subtitle}
                          </p>
                        </div>
                      </div>
                      <div className="ml-4 flex-shrink-0">
                        {isExpanded ? (
                          <ChevronUp size={24} style={{ color: 'var(--color-primary)' }} />
                        ) : (
                          <ChevronDown size={24} style={{ color: 'var(--color-text-secondary)' }} />
                        )}
                      </div>
                    </button>

                    {/* Expanded Content */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="p-6 pt-0 space-y-6">
                            {/* Overview */}
                            <div className="pt-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
                              <p className="text-lg italic mb-6" style={{ color: 'var(--color-text)' }}>
                                {principle.overview}
                              </p>
                            </div>

                            {/* Sections */}
                            {principle.sections.map((section, sectionIndex) => (
                              <div key={sectionIndex} className="space-y-3">
                                <h4 className="text-lg font-theme font-bold" style={{ color: 'var(--color-primary)' }}>
                                  {section.heading}
                                </h4>
                                {section.content.map((paragraph, i) => {
                                  // Handle bold markdown-style **text**
                                  if (paragraph.includes('**')) {
                                    const parts = paragraph.split(/(\*\*.*?\*\*)/g);
                                    return (
                                      <p key={i} style={{ color: 'var(--color-text)' }}>
                                        {parts.map((part, partIndex) => {
                                          if (part.startsWith('**') && part.endsWith('**')) {
                                            return (
                                              <strong key={partIndex} style={{ color: 'var(--color-primary)' }}>
                                                {part.slice(2, -2)}
                                              </strong>
                                            );
                                          }
                                          return part;
                                        })}
                                      </p>
                                    );
                                  }
                                  return (
                                    <p key={i} style={{ color: 'var(--color-text)' }}>
                                      {paragraph || <br />}
                                    </p>
                                  );
                                })}
                                {section.examples && (
                                  <ul className="space-y-1 mt-3">
                                    {section.examples.map((example, i) => (
                                      <li key={i} className="flex items-start space-x-2">
                                        <span style={{ color: 'var(--color-primary)' }}>•</span>
                                        <span style={{ color: 'var(--color-text-secondary)' }}>{example}</span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="p-8 rounded-xl"
            style={{
              backgroundColor: 'var(--color-bg-secondary)',
              borderColor: 'var(--color-border)',
              borderWidth: '1px',
              borderStyle: 'solid'
            }}
          >
            <h2 className="text-2xl md:text-3xl font-theme font-bold mb-4 text-gradient">
              Want to See It in Action?
            </h2>
            <p className="mb-6" style={{ color: 'var(--color-text)' }}>
              Check out our projects to see how we actually implement these principles. Spoiler: We try our best.
            </p>
            <Link
              to="/projects"
              className="inline-block px-6 py-3 rounded-lg font-semibold transition-all hover:scale-105"
              style={{
                background: 'var(--gradient-primary)',
                color: 'var(--color-bg)'
              }}
            >
              View Our Projects →
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Easter Egg Hint */}
      <section className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-sm italic"
            style={{ color: 'var(--color-text-secondary)' + '60' }}
          >
            By the way, if you haven't found all 5 secret themes yet, keep exploring.
            <br />
            Press the same number key (0-4) five times. You'll know when it works.
          </motion.p>
        </div>
      </section>
    </PageLayout>
  );
};

export default PhilosophyPageExpanded;
